import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import MainLayout from '../../components/layout/MainLayout';
import { listUserDirectory, updateUserInfo } from '../../api/backendApi';
import { useAuth } from '../../contexts/AuthContext';
import { directoryIdentity, identityLabel, rowsFrom, safeDealerError, tenantAccessText } from './dealerAccess';
import {
  PLATFORM_PERMISSIONS,
  isSuperadminRole,
  platformPermissionDraft,
} from './platformAccess';
import TenantSupportAccessTab from './TenantSupportAccessTab';

export const USER_CAPABILITIES = [
  { key: 'canConfigure', label: 'Can Configure' },
  { key: 'canTriggerEvents', label: 'Can Trigger Events' },
  { key: 'receivesNotifications', label: 'Receives Notifications' },
  { key: 'phoneEnabled', label: 'Enable browser phone' },
  { key: 'remindersEnabled', label: 'Enable reminders and timers' },
  { key: 'emailRemEnabled', label: 'Enable Email Reminders' },
];

export function capabilitiesFrom(user) {
  const raw = user?.capabilities && typeof user.capabilities === 'object' ? user.capabilities : {};
  const next = {};
  USER_CAPABILITIES.forEach((item) => {
    next[item.key] = raw[item.key] === true;
  });
  return next;
}

export function canEditDirectoryCapabilities(user) {
  if (!user?.id || !user.capabilities || typeof user.capabilities !== 'object') return false;
  return directoryIdentity(user) !== 'orphaned_access';
}

export function canOpenUserDialog(user, viewerRole) {
  if (!user?.id || directoryIdentity(user) === 'orphaned_access') return false;
  if (canEditDirectoryCapabilities(user)) return true;
  return isSuperadminRole(viewerRole);
}

function saveErrorMessage(err) {
  const raw = String(err?.message || '');
  const jsonStart = raw.indexOf('{');
  if (jsonStart >= 0) {
    try {
      const body = JSON.parse(raw.slice(jsonStart));
      if (body.error || body.message) return body.error || body.message;
    } catch {
      // The API error text is not JSON.
    }
  }
  return raw || 'Could not save capabilities';
}

const COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'role', label: 'Role' },
  { key: 'identity', label: 'Identity' },
  { key: 'tenants', label: 'Tenants' },
  { key: 'dealers', label: 'Dealers' },
  { key: 'dealerEmail', label: 'Dealer Email' },
];

export function namesFrom(items, key) {
  return (items || [])
    .map((item) => item?.name || item?.[key] || '')
    .filter(Boolean)
    .join(', ');
}

export function valuesFrom(items, key) {
  return (items || [])
    .map((item) => item?.[key] || '')
    .filter(Boolean)
    .join(', ');
}

export function userDirectoryName(user) {
  const fromParts = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  return String(user?.displayName || user?.name || fromParts || user?.email || '').trim() || '—';
}

function matches(value, term) {
  if (!term) return true;
  return String(value || '').toLowerCase().includes(term);
}

export function filterDirectoryRows(users, filters) {
  const name = String(filters.name || '').trim().toLowerCase();
  const email = String(filters.email || '').trim().toLowerCase();
  const role = String(filters.role || '').trim().toLowerCase();
  const tenant = String(filters.tenant || '').trim().toLowerCase();
  const dealer = String(filters.dealer || '').trim().toLowerCase();
  const dealerEmail = String(filters.dealerEmail || '').trim().toLowerCase();
  return (users || []).filter((user) => (
    matches(userDirectoryName(user), name)
    && matches(user.email, email)
    && matches(user.role, role)
    && matches(tenantAccessText(user) || namesFrom(user.tenants, 'tenantId') || user.tenantName, tenant)
    && matches(namesFrom(user.dealers, 'dealerId'), dealer)
    && matches(valuesFrom(user.dealers, 'contactEmail'), dealerEmail)
  ));
}

export function sortDirectoryRows(users, sort) {
  const key = sort?.key || 'name';
  const dir = sort?.dir === 'desc' ? -1 : 1;
  const valueFor = (user) => {
    if (key === 'name') return userDirectoryName(user);
    if (key === 'email') return user.email || '';
    if (key === 'role') return user.role || '';
    if (key === 'identity') return identityLabel(directoryIdentity(user));
    if (key === 'tenants') return tenantAccessText(user) || namesFrom(user.tenants, 'tenantId') || user.tenantName || '';
    if (key === 'dealers') return namesFrom(user.dealers, 'dealerId') || '';
    if (key === 'dealerEmail') return valuesFrom(user.dealers, 'contactEmail') || '';
    return '';
  };
  return [...(users || [])].sort((left, right) => {
    const cmp = String(valueFor(left)).localeCompare(String(valueFor(right)), undefined, { sensitivity: 'base' });
    return cmp * dir;
  });
}

function changedPlatformPermissions(before, after) {
  return PLATFORM_PERMISSIONS.filter((item) => before[item.key] !== after[item.key]);
}

function UserEditDialog({ user, canEditPlatform, canEditSupport, onClose, onSavedCapabilities, onSavedPlatform }) {
  const [tab, setTab] = useState('capabilities');
  const [capabilities, setCapabilities] = useState(() => capabilitiesFrom(user));
  const initialPermissions = platformPermissionDraft(user);
  const [permissions, setPermissions] = useState(initialPermissions);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const superadmin = user?.role === 'superadmin';
  const permissionChanges = changedPlatformPermissions(initialPermissions, permissions);
  const canEditCapabilities = canEditDirectoryCapabilities(user);

  const toggleCapability = (key) => (event) => {
    setCapabilities((prev) => ({ ...prev, [key]: event.target.checked }));
  };

  const togglePermission = (key) => (event) => {
    if (key === 'pricingManagement' && superadmin) return;
    setConfirming(false);
    setPermissions((prev) => ({ ...prev, [key]: event.target.checked }));
  };

  const saveCapabilities = async () => {
    setSaving(true);
    setSaveError('');
    const next = {};
    USER_CAPABILITIES.forEach((item) => {
      next[item.key] = capabilities[item.key] === true;
    });
    try {
      await updateUserInfo(user.id, { permissions: next });
      onSavedCapabilities(next);
    } catch (err) {
      setSaveError(saveErrorMessage(err));
      setSaving(false);
    }
  };

  const savePermissions = async () => {
    if (!permissionChanges.length) {
      onClose();
      return;
    }
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setSaving(true);
    setSaveError('');
    const next = superadmin ? { ...permissions, pricingManagement: true } : permissions;
    try {
      await updateUserInfo(user.id, { permissions: next });
      onSavedPlatform(next);
    } catch (err) {
      setSaveError(saveErrorMessage(err));
      setSaving(false);
      setConfirming(false);
    }
  };

  return (
    <Dialog
      open
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        style: { width: 'min(900px, calc(100% - 32px))', maxWidth: 'calc(100% - 32px)' },
      }}
    >
      <DialogTitle>
        {userDirectoryName(user)}
        {user?.email ? ` · ${user.email}` : ''}
      </DialogTitle>
      <Tabs
        value={tab}
        onChange={(_event, value) => {
          setTab(value);
          setConfirming(false);
          setSaveError('');
        }}
        sx={{ px: 3 }}
      >
        <Tab value="capabilities" label="Capabilities" />
        {canEditPlatform ? <Tab value="permissions" label="Platform permissions" /> : null}
        {canEditSupport ? <Tab value="support" label="Tenant Support Access" /> : null}
      </Tabs>
      <DialogContent>
        {saveError ? <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert> : null}
        {tab === 'support' ? (
          <TenantSupportAccessTab user={user} />
        ) : tab === 'capabilities' ? (
          <Box sx={{ display: 'grid', mt: 1 }}>
            {!canEditCapabilities ? (
              <Typography sx={{ mb: 1 }} color="text.secondary">
                This user has no capability record to edit. Tenant support access can still be assigned.
              </Typography>
            ) : null}
            {USER_CAPABILITIES.map((item) => (
              <FormControlLabel
                key={item.key}
                control={(
                  <Checkbox
                    checked={capabilities[item.key] === true}
                    onChange={toggleCapability(item.key)}
                    disabled={saving || !canEditCapabilities}
                  />
                )}
                label={item.label}
              />
            ))}
          </Box>
        ) : (
          <>
            <Typography sx={{ mt: 1, mb: 2 }} variant="body2">
              These are platform administrative capabilities. They do not change this person&apos;s tenant role and they do not make them a Superadmin.
            </Typography>
            {confirming ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {permissionChanges.map((item) => (
                  <div key={item.key}>
                    {permissions[item.key] ? 'Grant' : 'Revoke'} {item.name}.
                  </div>
                ))}
              </Alert>
            ) : null}
            <Box sx={{ display: 'grid' }}>
              {PLATFORM_PERMISSIONS.map((item) => (
                <FormControlLabel
                  key={item.key}
                  control={(
                    <Checkbox
                      checked={permissions[item.key] === true}
                      onChange={togglePermission(item.key)}
                      disabled={saving || (item.key === 'pricingManagement' && superadmin)}
                      inputProps={{ 'aria-label': item.name }}
                    />
                  )}
                  label={(
                    <Box>
                      <Typography>{item.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{item.description}</Typography>
                    </Box>
                  )}
                />
              ))}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        {tab === 'support' ? null : tab === 'capabilities' ? (
          <Button variant="contained" onClick={saveCapabilities} disabled={saving || !canEditCapabilities}>
            {saving ? 'Saving…' : 'Save capabilities'}
          </Button>
        ) : (
          <Button variant="contained" onClick={savePermissions} disabled={saving || (!permissionChanges.length && !confirming)}>
            {saving ? 'Saving…' : confirming ? 'Confirm change' : 'Save platform permissions'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default function AllUsersDirectoryPage() {
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ name: '', email: '', role: '', tenant: '', dealer: '', dealerEmail: '' });
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const auth = useAuth() || {};
  const viewerRole = auth.role || auth.userData?.role || auth.userClaims?.role;
  const canEditPlatform = !viewerRole || isSuperadminRole(viewerRole);
  const canEditSupport = isSuperadminRole(viewerRole);

  useEffect(() => {
    setLoading(true);
    listUserDirectory()
      .then((response) => setUsers(rowsFrom(response)))
      .catch((err) => setError(safeDealerError(err)))
      .finally(() => setLoading(false));
  }, []);

  const rows = useMemo(
    () => sortDirectoryRows(filterDirectoryRows(users, filters), sort),
    [users, filters, sort]
  );

  const setFilter = (key) => (event) => {
    setFilters((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const toggleSort = (key) => {
    setSort((prev) => (
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    ));
  };

  return (
    <MainLayout scrollMain>
      <Box sx={{ p: 3, maxWidth: 1400 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>All Users</Typography>
        <Typography sx={{ mb: 2 }} color="text.secondary">
          Superadmin directory of users across every tenant and Dealer.
        </Typography>
        {error ? <Alert severity="error" sx={{ my: 2 }}>{error}</Alert> : null}
        <Box sx={{ display: 'grid', gap: 2, mb: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(6, minmax(0, 1fr))' } }}>
          <TextField label="User" value={filters.name} onChange={setFilter('name')} size="small" />
          <TextField label="Email" value={filters.email} onChange={setFilter('email')} size="small" />
          <TextField label="Role" value={filters.role} onChange={setFilter('role')} size="small" />
          <TextField label="Tenant" value={filters.tenant} onChange={setFilter('tenant')} size="small" />
          <TextField label="Dealer" value={filters.dealer} onChange={setFilter('dealer')} size="small" />
          <TextField label="Dealer email" value={filters.dealerEmail} onChange={setFilter('dealerEmail')} size="small" />
        </Box>
        {loading ? <Typography>Loading users…</Typography> : null}
        {!loading ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                {COLUMNS.map((column) => (
                  <TableCell key={column.key} sortDirection={sort.key === column.key ? sort.dir : false} sx={{ fontWeight: 600 }}>
                    <TableSortLabel
                      active={sort.key === column.key}
                      direction={sort.key === column.key ? sort.dir : 'asc'}
                      onClick={() => toggleSort(column.key)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length}>No users found.</TableCell>
                </TableRow>
              ) : rows.map((user) => {
                const identity = directoryIdentity(user);
                const editable = canOpenUserDialog(user, viewerRole);
                return (
                <TableRow
                  key={user.id}
                  data-identity={identity}
                  hover={editable}
                  onClick={editable ? () => setEditing(user) : undefined}
                  sx={editable ? { cursor: 'pointer' } : undefined}
                >
                  <TableCell>{identity === 'orphaned_access' ? 'Orphaned access' : userDirectoryName(user)}</TableCell>
                  <TableCell>{identity === 'orphaned_access' ? '—' : (user.email || '—')}</TableCell>
                  <TableCell>{user.role || '—'}</TableCell>
                  <TableCell>{identityLabel(identity)}</TableCell>
                  <TableCell>
                    {identity === 'dealer_only'
                      ? 'Dealer only'
                      : (tenantAccessText(user) || namesFrom(user.tenants, 'tenantId') || user.tenantName || '—')}
                  </TableCell>
                  <TableCell>{namesFrom(user.dealers, 'dealerId') || '—'}</TableCell>
                  <TableCell>{valuesFrom(user.dealers, 'contactEmail') || '—'}</TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : null}
        {editing ? (
          <UserEditDialog
            user={editing}
            canEditPlatform={canEditPlatform}
            canEditSupport={canEditSupport}
            onClose={() => setEditing(null)}
            onSavedCapabilities={(permissions) => {
              setUsers((prev) => prev.map((row) => (
                row.id === editing.id ? { ...row, capabilities: permissions } : row
              )));
              setEditing(null);
            }}
            onSavedPlatform={(permissions) => {
              setUsers((prev) => prev.map((row) => (
                row.id === editing.id ? { ...row, platformPermissions: permissions } : row
              )));
              setEditing(null);
            }}
          />
        ) : null}
      </Box>
    </MainLayout>
  );
}
